import { useEffect, useState } from "react";
import type { FormEvent } from "react";
import { Link, useParams } from "react-router-dom";
import { ConfirmModal } from "../components/ConfirmModal";
import { Shell } from "../components/Shell";
import { WhatsappAdminVisor } from "../components/WhatsappAdminVisor";
import {
  ApiError,
  tenants as tenantsApi,
  whatsapp as wa,
} from "../lib/api";
import type { Tenant, WhatsappCredsBody, WhatsappCredsOut } from "../lib/api";

export function TenantWhatsappPage() {
  const { id } = useParams<{ id: string }>();
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [creds, setCreds] = useState<WhatsappCredsOut | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showDeactivate, setShowDeactivate] = useState(false);
  const [ok, setOk] = useState<string | null>(null);

  const [form, setForm] = useState<WhatsappCredsBody>({
    phone_number_id: "",
    waba_id: "",
    display_phone_number: "",
    access_token: "",
    app_secret: "",
    verify_token: "",
  });
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);

  // Modal "Editar campos" — edición parcial (PATCH)
  const [showEdit, setShowEdit] = useState(false);
  const [editForm, setEditForm] = useState({
    phone_number_id: "",
    waba_id: "",
    display_phone_number: "",
    access_token: "",
    app_secret: "",
    verify_token: "",
  });
  const [editBusy, setEditBusy] = useState(false);

  const openEdit = () => {
    if (!creds) return;
    setEditForm({
      phone_number_id: creds.phone_number_id || "",
      waba_id: creds.waba_id || "",
      display_phone_number: creds.display_phone_number || "",
      access_token: "", // sensible — vacío = no cambiar
      app_secret: "", // sensible — vacío = no cambiar
      verify_token: creds.verify_token || "",
    });
    setShowEdit(true);
  };

  const submitEdit = async () => {
    if (!id || !creds) return;
    setEditBusy(true);
    setError(null);
    setOk(null);
    try {
      // Solo enviamos los campos que el usuario haya cambiado:
      // - waba_id y display_phone_number aceptan string vacío = limpiar.
      // - los demás se ignoran si están vacíos (no se borran desde acá).
      const body: Record<string, string> = {};
      if (editForm.phone_number_id.trim() &&
          editForm.phone_number_id.trim() !== creds.phone_number_id) {
        body.phone_number_id = editForm.phone_number_id.trim();
      }
      if (editForm.waba_id !== (creds.waba_id ?? "")) {
        body.waba_id = editForm.waba_id.trim();
      }
      if (
        editForm.display_phone_number !==
        (creds.display_phone_number ?? "")
      ) {
        body.display_phone_number = editForm.display_phone_number.trim();
      }
      if (editForm.verify_token.trim() &&
          editForm.verify_token.trim() !== creds.verify_token) {
        body.verify_token = editForm.verify_token.trim();
      }
      if (editForm.access_token.trim()) {
        body.access_token = editForm.access_token.trim();
      }
      if (editForm.app_secret.trim()) {
        body.app_secret = editForm.app_secret.trim();
      }
      if (Object.keys(body).length === 0) {
        setShowEdit(false);
        setOk("Sin cambios.");
        setTimeout(() => setOk(null), 2000);
        return;
      }
      const c = await wa.patch(id, body);
      setCreds(c);
      setShowEdit(false);
      setOk("Cambios guardados.");
      setTimeout(() => setOk(null), 3000);
    } catch (err) {
      setError(
        err instanceof ApiError
          ? typeof err.detail === "string"
            ? err.detail
            : err.message
          : "Error guardando",
      );
    } finally {
      setEditBusy(false);
    }
  };

  const load = async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const t = await tenantsApi.get(id);
      setTenant(t);
      try {
        const c = await wa.get(id);
        setCreds(c);
        setEditing(false);
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) {
          setCreds(null);
          setEditing(true);
        } else {
          throw err;
        }
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Error de red");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!id) return;
    setBusy(true);
    setError(null);
    setOk(null);
    try {
      const c = await wa.upsert(id, {
        phone_number_id: form.phone_number_id.trim(),
        waba_id: form.waba_id?.trim() || undefined,
        display_phone_number: form.display_phone_number?.trim() || undefined,
        access_token: form.access_token.trim(),
        app_secret: form.app_secret?.trim() || undefined,
        verify_token: form.verify_token?.trim() || undefined,
      });
      setCreds(c);
      setEditing(false);
      setForm((f) => ({ ...f, access_token: "", app_secret: "" }));
      setOk("Credenciales guardadas. Configura el webhook en Meta con los datos de abajo.");
    } catch (err) {
      setError(
        err instanceof ApiError
          ? typeof err.detail === "string"
            ? err.detail
            : err.message
          : "Error guardando",
      );
    } finally {
      setBusy(false);
    }
  };

  const deactivate = async () => {
    if (!id) return;
    setBusy(true);
    try {
      await wa.deactivate(id);
      setShowDeactivate(false);
      await load();
      setOk("Canal desactivado.");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Error de red");
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <Shell>
        <section className="page">
          <p style={{ color: "var(--ink-3)" }}>Cargando…</p>
        </section>
      </Shell>
    );
  }

  return (
    <Shell>
      <section className="page" style={{ maxWidth: 820 }}>
        <div className="eyebrow">
          <Link to="/tenants">Alcaldías</Link> · {tenant?.slug} · WhatsApp
        </div>
        <h1>Canal WhatsApp · {tenant?.nombre}</h1>
        <p className="lede">
          Configura las credenciales de WhatsApp Cloud API. La alcaldía debe
          tener una cuenta WhatsApp Business y un Phone Number ID asignado en
          el panel de desarrolladores de Meta.
        </p>

        {error && <div className="alert err">{error}</div>}
        {ok && <div className="alert ok">{ok}</div>}

        {creds && !editing && (
          <div className="card" style={{ marginBottom: 24 }}>
            <div className="card-inner">
              <div className="eyebrow">Estado</div>
              <h2 style={{ marginTop: 4 }}>
                {creds.active ? (
                  <span style={{ color: "var(--ok)" }}>● Activo</span>
                ) : (
                  <span style={{ color: "var(--err)" }}>● Desactivado</span>
                )}
              </h2>

              <Field label="Phone Number ID" mono>
                {creds.phone_number_id}
              </Field>
              {creds.display_phone_number && (
                <Field label="Número visible">{creds.display_phone_number}</Field>
              )}
              {creds.waba_id && (
                <Field label="WABA ID" mono>
                  {creds.waba_id}
                </Field>
              )}
              <Field label="Webhook URL (configurar en Meta)" mono>
                {creds.webhook_url}
              </Field>
              <Field label="Verify Token (configurar en Meta)" mono>
                {creds.verify_token}
              </Field>
              <Field label="Verificación HMAC del webhook">
                {creds.has_app_secret ? (
                  <span style={{ color: "var(--ok)" }}>
                    ● Activa — firmas verificadas
                  </span>
                ) : (
                  <span style={{ color: "var(--warn)" }}>
                    ⚠ Inactiva — acepta sin verificar firma. Configura el App
                    Secret cuanto antes.
                  </span>
                )}
              </Field>

              <div style={{ display: "flex", gap: 12, marginTop: 18, flexWrap: "wrap" }}>
                <button
                  type="button"
                  className="logout-btn"
                  onClick={openEdit}
                  style={{
                    background: "var(--ink)",
                    color: "#fff",
                    borderColor: "var(--ink)",
                  }}
                >
                  Editar campos
                </button>
                <button
                  type="button"
                  className="logout-btn"
                  onClick={() => setEditing(true)}
                  title="Reemplaza TODOS los datos. Útil si rotaste el token o el número."
                >
                  Reemplazar todo
                </button>
                {creds.active && (
                  <button
                    type="button"
                    className="logout-btn"
                    onClick={() => setShowDeactivate(true)}
                    disabled={busy}
                  >
                    Desactivar canal
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {creds && creds.active && id && !editing && (
          <WhatsappAdminVisor tenantId={id} />
        )}

        {editing && (
          <form className="card" onSubmit={submit}>
            <div className="card-inner">
              <div className="eyebrow">Credenciales WhatsApp Cloud API</div>
              <h2 style={{ marginTop: 4 }}>
                {creds ? "Reemplazar credenciales" : "Configurar canal"}
              </h2>
              <p className="helper" style={{ marginBottom: 18 }}>
                Obtén estos datos en{" "}
                <a
                  href="https://developers.facebook.com/apps"
                  target="_blank"
                  rel="noreferrer"
                >
                  developers.facebook.com
                </a>{" "}
                → tu app → WhatsApp → API Setup.
              </p>

              <div className="form-row">
                <label className="label">Phone Number ID</label>
                <input
                  className="input"
                  value={form.phone_number_id}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, phone_number_id: e.target.value }))
                  }
                  placeholder="106540352242922"
                  required
                  style={{ fontFamily: "var(--font-mono)" }}
                />
              </div>

              <div className="form-row">
                <label className="label">Access Token (permanente o de larga duración)</label>
                <textarea
                  className="input"
                  value={form.access_token}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, access_token: e.target.value }))
                  }
                  placeholder="EAA..."
                  required
                  rows={4}
                  style={{
                    fontFamily: "var(--font-mono)",
                    fontSize: 12,
                    resize: "vertical",
                  }}
                />
                <p className="helper">
                  Se cifra con la KEK del sistema antes de guardarlo. No queda en texto plano.
                </p>
              </div>

              <div className="form-row">
                <label className="label">
                  App Secret de Meta (recomendado — habilita verificación HMAC del webhook)
                </label>
                <input
                  className="input"
                  type="password"
                  value={form.app_secret ?? ""}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, app_secret: e.target.value }))
                  }
                  placeholder="(dejar vacío para no cambiar)"
                  style={{ fontFamily: "var(--font-mono)" }}
                />
                <p className="helper">
                  Meta → tu app → Configuración → Básica → App Secret. Sin esto,
                  el webhook acepta sin verificar firma (modo legacy).
                </p>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 18 }}>
                <div className="form-row">
                  <label className="label">WABA ID (opcional)</label>
                  <input
                    className="input"
                    value={form.waba_id ?? ""}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, waba_id: e.target.value }))
                    }
                    placeholder="WhatsApp Business Account ID"
                    style={{ fontFamily: "var(--font-mono)" }}
                  />
                </div>
                <div className="form-row">
                  <label className="label">Número visible (opcional)</label>
                  <input
                    className="input"
                    value={form.display_phone_number ?? ""}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        display_phone_number: e.target.value,
                      }))
                    }
                    placeholder="+57 300 000 0000"
                  />
                </div>
              </div>

              <div className="form-row">
                <label className="label">Verify Token (opcional — se genera uno seguro si lo dejas vacío)</label>
                <input
                  className="input"
                  value={form.verify_token ?? ""}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, verify_token: e.target.value }))
                  }
                  placeholder="(generación automática)"
                  style={{ fontFamily: "var(--font-mono)" }}
                />
              </div>

              <div style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>
                {creds && (
                  <button
                    type="button"
                    className="logout-btn"
                    onClick={() => setEditing(false)}
                    disabled={busy}
                  >
                    Cancelar
                  </button>
                )}
                <button
                  className="cta"
                  type="submit"
                  disabled={busy || !form.phone_number_id || !form.access_token}
                  style={{ maxWidth: 240 }}
                >
                  <span className="cta-inner">
                    {busy && <span className="spinner" aria-hidden />}
                    Guardar credenciales
                  </span>
                </button>
              </div>
            </div>
          </form>
        )}
      </section>

      {/* Modal Editar campos (PATCH parcial) */}
      {showEdit && (
        <div
          onClick={() => !editBusy && setShowEdit(false)}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.5)",
            display: "grid",
            placeItems: "center",
            zIndex: 100,
            padding: 16,
            overflowY: "auto",
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="card"
            style={{ maxWidth: 640, width: "100%" }}
          >
            <div className="card-inner">
              <h3 style={{ marginTop: 0 }}>Editar campos</h3>
              <p
                style={{ color: "var(--ink-3)", fontSize: 13, lineHeight: 1.5 }}
              >
                Cambia solo los campos que necesites. <strong>Los campos sensibles
                (Access Token / App Secret) están vacíos a propósito: si los
                dejas así no se modifican</strong>. Para borrar el WABA ID o el
                Número visible, vacía esos campos.
              </p>

              {/* Errores y feedback INSIDE el modal para que sean visibles */}
              {error && (
                <div
                  className="alert err"
                  style={{ marginTop: 8, marginBottom: 8 }}
                >
                  {error}
                </div>
              )}
              {ok && (
                <div
                  className="alert ok"
                  style={{ marginTop: 8, marginBottom: 8 }}
                >
                  {ok}
                </div>
              )}

              <div className="form-row" style={{ marginTop: 14 }}>
                <label className="label">Phone Number ID</label>
                <input
                  className="input"
                  value={editForm.phone_number_id}
                  onChange={(e) =>
                    setEditForm((f) => ({ ...f, phone_number_id: e.target.value }))
                  }
                  style={{ fontFamily: "var(--font-mono)" }}
                />
              </div>

              <div className="form-row">
                <label className="label">WABA ID</label>
                <input
                  className="input"
                  value={editForm.waba_id}
                  onChange={(e) =>
                    setEditForm((f) => ({ ...f, waba_id: e.target.value }))
                  }
                  placeholder="123456789012345"
                  style={{ fontFamily: "var(--font-mono)" }}
                />
              </div>

              <div className="form-row">
                <label className="label">Número visible</label>
                <input
                  className="input"
                  value={editForm.display_phone_number}
                  onChange={(e) =>
                    setEditForm((f) => ({
                      ...f,
                      display_phone_number: e.target.value,
                    }))
                  }
                  placeholder="+57 320 200 5236"
                />
              </div>

              <div className="form-row">
                <label className="label">Verify Token</label>
                <input
                  className="input"
                  value={editForm.verify_token}
                  onChange={(e) =>
                    setEditForm((f) => ({ ...f, verify_token: e.target.value }))
                  }
                  style={{ fontFamily: "var(--font-mono)" }}
                />
              </div>

              <div className="form-row">
                <label className="label">
                  Access Token{" "}
                  <span style={{ color: "var(--ink-3)", fontWeight: 400 }}>
                    (dejar vacío para no cambiar)
                  </span>
                </label>
                <textarea
                  className="input"
                  value={editForm.access_token}
                  onChange={(e) =>
                    setEditForm((f) => ({ ...f, access_token: e.target.value }))
                  }
                  rows={3}
                  placeholder="EAA…  ←  solo pegar si quieres cambiarlo"
                  style={{
                    fontFamily: "var(--font-mono)",
                    fontSize: 12,
                    resize: "vertical",
                  }}
                />
              </div>

              <div className="form-row">
                <label className="label">
                  App Secret{" "}
                  <span style={{ color: "var(--ink-3)", fontWeight: 400 }}>
                    (dejar vacío para no cambiar)
                  </span>
                </label>
                <input
                  className="input"
                  type="password"
                  value={editForm.app_secret}
                  onChange={(e) =>
                    setEditForm((f) => ({ ...f, app_secret: e.target.value }))
                  }
                  placeholder="(solo pegar si quieres cambiarlo)"
                  style={{ fontFamily: "var(--font-mono)" }}
                />
              </div>

              <div
                style={{
                  display: "flex",
                  gap: 8,
                  justifyContent: "flex-end",
                  marginTop: 18,
                }}
              >
                <button
                  type="button"
                  className="logout-btn"
                  onClick={() => setShowEdit(false)}
                  disabled={editBusy}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  className="logout-btn"
                  onClick={() => void submitEdit()}
                  disabled={editBusy}
                  style={{
                    background: "var(--ink)",
                    color: "#fff",
                    borderColor: "var(--ink)",
                  }}
                >
                  {editBusy ? "Guardando…" : "Guardar cambios"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        open={showDeactivate}
        title="¿Desactivar el canal WhatsApp?"
        message="Los ciudadanos que escriban al número configurado dejarán de recibir respuesta automática. Puedes volver a activarlo más tarde."
        confirmLabel="Sí, desactivar"
        variant="danger"
        busy={busy}
        onCancel={() => setShowDeactivate(false)}
        onConfirm={() => void deactivate()}
      />
    </Shell>
  );
}

function Field({
  label,
  mono,
  children,
}: {
  label: string;
  mono?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="form-row" style={{ marginBottom: 14 }}>
      <div className="label">{label}</div>
      <div
        style={{
          background: "var(--bone)",
          border: "1px solid var(--hairline)",
          padding: "10px 12px",
          fontFamily: mono ? "var(--font-mono)" : "var(--font-app)",
          fontSize: mono ? 12 : 14,
          wordBreak: "break-all",
        }}
      >
        {children}
      </div>
    </div>
  );
}
